<template>
  <div class="tw-p-4 sm:tw-p-6">
    <div class="tw-mb-4 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
      <div class="tw-text-left">
        <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">Drivers</h3>
        <div class="tw-mt-1 tw-text-sm tw-text-slate-600">Manage your driver roster.</div>
      </div>

      <button
        type="button"
        class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
        @click="openCreate"
      >
        Add driver
      </button>
    </div>

    <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50 tw-p-3 sm:tw-p-4">
      <div class="tw-mb-3">
        <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
          <input type="checkbox" class="tw-h-4 tw-w-4" v-model="showActiveOnly" />
          Show active drivers only
        </label>
      </div>

      <div class="tw-overflow-x-auto">
        <table class="tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-left">
          <thead>
            <tr class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Name</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Phone</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Email</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Status</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Truck #</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3"></th>
            </tr>
          </thead>
          <tbody class="tw-divide-y tw-divide-slate-200/70">
            <tr
              v-for="driver in filteredDrivers"
              :key="driver.id"
              class="tw-cursor-pointer hover:tw-bg-slate-50/70"
              @click="openEdit(driver)"
            >
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-900">{{ driver.fullName || driver.name }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">{{ driver.phoneNumber }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">{{ driver.email || '—' }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-sm">
                <span
                  class="tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold"
                  :class="driver.isActive ? 'tw-bg-emerald-100 tw-text-emerald-900' : 'tw-bg-slate-200 tw-text-slate-800'"
                >
                  {{ driver.isActive ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">
                {{ (driver.truck && driver.truck.length > 0 && driver.truck[0].truckNumber) || '—' }}
              </td>
              <td class="tw-px-3 tw-py-3 tw-text-right">
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-800 hover:tw-bg-white"
                  @click.stop="openEdit(driver)"
                >
                  Edit
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Modal
      v-model:isOpen="isModalOpen"
      modalId="driver-modal"
      :title="modalMode === 'create' ? 'Add Driver' : 'Edit Driver'"
      maxWidth="2xl"
    >
      <template #content="{ close }">
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div>
                <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Driver Name</label>
                <input
                  v-model="form.fullName"
                  type="text"
                  class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
                />
              </div>

              <div>
                <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Driver Phone #</label>
                <input
                  v-model="form.phoneNumber"
                  type="text"
                  class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
                />
              </div>

              <div class="md:tw-col-span-2">
                <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Driver Email</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
                />
              </div>

              <div class="md:tw-col-span-2">
                <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
                  <input type="checkbox" class="tw-h-4 tw-w-4" v-model="form.isOwnerOperator" />
                  Is Owner Operator?
                </label>
              </div>

              <div class="md:tw-col-span-2">
                <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
                  <input type="checkbox" class="tw-h-4 tw-w-4" v-model="form.isActive" />
                  Is Active?
                </label>
              </div>

              <div class="md:tw-col-span-2">
                <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Truck</label>
                <div class="tw-mt-1">
                  <FilterableDropdown
                    v-model="form.truckId"
                    :options="trucks"
                    label="truckNumber"
                    :reduce="(truck: any) => truck.id"
                    class="tw-w-full"
                    :components="{ OpenIndicator: null, Deselect: null }"
                  ></FilterableDropdown>
                </div>
              </div>
            </div>

        <div v-if="formError" class="tw-mt-4 tw-text-sm tw-text-rose-600">{{ formError }}</div>

        <div class="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-800 hover:tw-bg-slate-50"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800 disabled:tw-opacity-60"
            :disabled="isSaving"
            @click="submit"
          >
            <font-awesome-icon v-if="isSaving" icon="circle-notch" spin class="tw-h-4 tw-w-4" />
            <font-awesome-icon v-else icon="check" class="tw-h-4 tw-w-4" />
            Save
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useApolloClient, useQuery } from '@vue/apollo-composable';
import DriversQuery from '../../gql/orders/Drivers.gql';
import TrucksQuery from '../../gql/drivers/Trucks.gql';
import CreateDriverMutation from '../../gql/drivers/CreateDriver.gql';
import UpdateDriverMutation from '../../gql/drivers/UpdateDriver.gql';

import Modal from '@/components/core/Modal/Modal.vue';
import FilterableDropdown from '../core/FilterableDropdown.vue';

const apollo = useApolloClient();

const { result, refetch } = useQuery(DriversQuery);

const trucksQuery = useQuery(TrucksQuery);

const drivers = computed(() => {
  const user = result.value?.user;
  return user?.drivers || [];
});

const trucks = computed(() => trucksQuery.result.value?.trucks || []);

const showActiveOnly = ref(true);
const filteredDrivers = computed(() => {
  const list = drivers.value || [];
  return showActiveOnly.value ? list.filter((d: any) => d.isActive) : list;
});

const isModalOpen = ref(false);
const modalMode = ref<'create' | 'edit'>('create');
const editingDriverId = ref<number | null>(null);
const isSaving = ref(false);
const formError = ref<string | null>(null);

const form = reactive({
  fullName: '',
  phoneNumber: '',
  email: '',
  isOwnerOperator: false,
  isActive: true,
  truckId: null as number | null,
});

function resetForm() {
  form.fullName = '';
  form.phoneNumber = '';
  form.email = '';
  form.isOwnerOperator = false;
  form.isActive = true;
  form.truckId = null;
  formError.value = null;
}

function openCreate() {
  modalMode.value = 'create';
  editingDriverId.value = null;
  resetForm();
  isModalOpen.value = true;
}

function openEdit(driver: any) {
  modalMode.value = 'edit';
  editingDriverId.value = parseInt(String(driver.id), 10);
  form.fullName = driver.fullName || driver.name || '';
  form.phoneNumber = driver.phoneNumber || '';
  form.email = driver.email || '';
  form.isOwnerOperator = !!driver.isOwnerOperator;
  form.isActive = driver.isActive !== false;
  form.truckId = driver.truck && driver.truck.length > 0 ? driver.truck[0].id : null;
  formError.value = null;
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
}

function createdDriver() {
  refetch();
}

function refreshUserData() {
  refetch();
}

async function submit() {
  if (isSaving.value) return;
  formError.value = null;

  if (!form.fullName || !form.phoneNumber) {
    formError.value = 'Driver name and phone number are required.';
    return;
  }

  isSaving.value = true;
  try {
    const normalizedTruckId =
      form.truckId === null || form.truckId === undefined || form.truckId === ''
        ? null
        : Number.parseInt(String(form.truckId), 10);

    if (modalMode.value === 'create') {
      await apollo.client.mutate({
        mutation: CreateDriverMutation,
        variables: {
          module: 'CONTRAK',
          phone: form.phoneNumber,
          name: form.fullName,
          email: form.email || null,
        },
      });
    } else {
      await apollo.client.mutate({
        mutation: UpdateDriverMutation,
        variables: {
          module: 'CONTRAK',
          id: editingDriverId.value,
          phone: form.phoneNumber,
          name: form.fullName,
          email: form.email || null,
          isOwnerOperator: form.isOwnerOperator,
          isActive: form.isActive,
          truckId: Number.isNaN(normalizedTruckId as number) ? null : normalizedTruckId,
        },
      });
    }

    await refetch();
    closeModal();
  } catch (e: any) {
    formError.value = e?.message || String(e);
  } finally {
    isSaving.value = false;
  }
}
</script>
